import axios from "axios";
import { supabase } from "./supabaseClient";

// Obter API URL do sistema de configuração dinâmica
const getApiUrl = () => {
  return (
    window.ENV?.REACT_APP_API_URL ||
    process.env.REACT_APP_API_URL ||
    "/api"
  );
};

const API_URL = getApiUrl();

const axiosInstance = axios.create({
  baseURL: API_URL,
});

export const reportService = {
  async getReports(filters = {}) {
    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      const token = session?.access_token;

      if (!token) {
        throw new Error(
          "Usuário não autenticado. Faça login para buscar relatórios."
        );
      }

      const response = await axiosInstance.get(`/reports`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params: filters,
      });
      return response.data;
    } catch (error) {
      if (error.response) {
        throw new Error(
          error.response.data.message || "Erro ao buscar relatórios"
        );
      }
      throw error;
    }
  },

  async generateReport(data) {
    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      const token = session?.access_token;

      if (!token) {
        throw new Error(
          "Usuário não autenticado. Faça login para gerar relatórios."
        );
      }

      // data.format should be 'xlsx' or 'pdf'
      const response = await axiosInstance.post(`/reports/generate`, data, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return response.data;
    } catch (error) {
      if (error.response) {
        throw new Error(
          error.response.data.message || "Erro ao gerar relatório"
        );
      }
      throw error;
    }
  },

  async deleteReport(reportId) {
    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      const token = session?.access_token;

      if (!token) {
        throw new Error(
          "Usuário não autenticado. Faça login para excluir relatórios."
        );
      }

      const response = await axiosInstance.delete(`/reports/${reportId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return response.data;
    } catch (error) {
      if (error.response) {
        throw new Error(
          error.response.data.message || "Erro ao excluir relatório"
        );
      }
      throw error;
    }
  },

  async downloadReport(reportId) {
    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      const token = session?.access_token;

      if (!token) {
        throw new Error(
          "Usuário não autenticado. Faça login para baixar relatórios."
        );
      }

      const response = await axiosInstance.get(`/reports/download/${reportId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        responseType: "blob",
      });

      // Determine MIME type from response header
      const contentType = response.headers["content-type"] || "";
      const isPdf = contentType.includes("pdf");
      const mimeType = isPdf ? "application/pdf" : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

      // Criar URL e fazer download
      const url = window.URL.createObjectURL(new Blob([response.data], { type: mimeType }));
      const link = document.createElement("a");
      link.href = url;

      // Extrair nome do arquivo do header ou usar padrão
      const contentDisposition = response.headers["content-disposition"];
      let fileName = isPdf ? "relatorio.pdf" : "relatorio.xlsx";
      if (contentDisposition) {
        const match = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (match && match[1]) {
          fileName = match[1].replace(/['"]/g, "");
        }
      }

      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      return true;
    } catch (error) {
      if (error.response) {
        throw new Error(
          error.response.data.message || "Erro ao baixar relatório"
        );
      }
      throw error;
    }
  },

  // Buscar relatório de clientes com detalhes (incluindo nome_contato)
  async getClientsReport(filters = {}) {
    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      const token = session?.access_token;

      if (!token) {
        throw new Error(
          "Usuário não autenticado. Faça login para buscar relatório de clientes."
        );
      }

      const response = await axiosInstance.get(`/reports/clients`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params: filters,
      });
      return response.data;
    } catch (error) {
      if (error.response) {
        throw new Error(
          error.response.data.message || "Erro ao buscar relatório de clientes"
        );
      }
      throw error;
    }
  },

  // Buscar relatório de pedidos com nome do contato do cliente
  async getOrdersReport(filters = {}) {
    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      const token = session?.access_token;

      if (!token) {
        throw new Error(
          "Usuário não autenticado. Faça login para buscar relatório de pedidos."
        );
      }

      const response = await axiosInstance.get(`/reports/orders`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params: filters,
      });
      return response.data;
    } catch (error) {
      if (error.response) {
        throw new Error(
          error.response.data.message || "Erro ao buscar relatório de pedidos"
        );
      }
      throw error;
    }
  },

  // Buscar relatório de produtos com informações de variantes
  async getProductsReport(filters = {}) {
    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      const token = session?.access_token;

      if (!token) {
        throw new Error(
          "Usuário não autenticado. Faça login para buscar relatório de produtos."
        );
      }

      const response = await axiosInstance.get(`/reports/products`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params: filters,
      });
      return response.data;
    } catch (error) {
      if (error.response) {
        throw new Error(
          error.response.data.message || "Erro ao buscar relatório de produtos"
        );
      }
      throw error;
    }
  },

  // Buscar resumo consolidado
  async getSummaryReport() {
    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      const token = session?.access_token;

      if (!token) {
        throw new Error(
          "Usuário não autenticado. Faça login para buscar relatório resumido."
        );
      }

      const response = await axiosInstance.get(`/reports/summary`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return response.data;
    } catch (error) {
      if (error.response) {
        throw new Error(
          error.response.data.message || "Erro ao buscar relatório resumido"
        );
      }
      throw error;
    }
  },
};
